import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, LinearProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const severityStyles = {
  success: {
    label: 'Success',
    borderColor: '#86efac',
    background: 'linear-gradient(180deg, #f6fff8 0%, #ecfdf3 100%)',
    shadow: '0 8px 24px rgba(34, 197, 94, 0.1)',
    labelColor: '#16a34a',
    closeColor: '#166534',
    messageColor: '#14532d',
    progressTrack: '#bbf7d0',
    progressBar: '#22c55e'
  },
  info: {
    label: 'Info',
    borderColor: '#bfdbfe',
    background: 'linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)',
    shadow: '0 8px 24px rgba(59, 130, 246, 0.08)',
    labelColor: '#2563eb',
    closeColor: '#1e3a8a',
    messageColor: '#1f2937',
    progressTrack: '#dbeafe',
    progressBar: '#3b82f6'
  },
  warning: {
    label: 'Warning',
    borderColor: '#fcd34d',
    background: 'linear-gradient(180deg, #fffdf5 0%, #fff7db 100%)',
    shadow: '0 8px 24px rgba(245, 158, 11, 0.1)',
    labelColor: '#d97706',
    closeColor: '#92400e',
    messageColor: '#422006',
    progressTrack: '#fde68a',
    progressBar: '#f59e0b'
  },
  error: {
    label: 'Error',
    borderColor: '#fda4af',
    background: 'linear-gradient(180deg, #fff8f8 0%, #fff1f2 100%)',
    shadow: '0 8px 24px rgba(239, 68, 68, 0.1)',
    labelColor: '#dc2626',
    closeColor: '#b91c1c',
    messageColor: '#4c0519',
    progressTrack: '#fecdd3',
    progressBar: '#ef4444'
  }
};

function stringifyValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'No details provided.';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseFeedbackData(data) {
  if (!data) {
    return {};
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return { additional_data: data };
    }
  }

  return data;
}

function resolveSeverity(parsedData) {
  const rawSeverity = (parsedData?.severity ?? parsedData?.data?.severity ?? parsedData?.type ?? '').toString().toLowerCase();

  if (rawSeverity.includes('error')) {
    return 'error';
  }

  if (rawSeverity.includes('warning')) {
    return 'warning';
  }

  if (rawSeverity.includes('success')) {
    return 'success';
  }

  return 'info';
}

export default function ControlFeedbackItem({ data, onClose, autoCloseMs = 5000 }) {
  const parsedData = parseFeedbackData(data);
  const details = parsedData?.additional_data ?? parsedData;
  const detailText = stringifyValue(details);
  const severity = resolveSeverity(parsedData);
  const styles = severityStyles[severity] ?? severityStyles.info;
  const isPersistent = autoCloseMs <= 0;
  const onCloseRef = useRef(onClose);

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (isPersistent) {
      return undefined;
    }

    const startedAt = Date.now();

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min((elapsed / autoCloseMs) * 100, 100);

      setProgress(nextProgress);

      if (nextProgress >= 100) {
        window.clearInterval(interval);
        onCloseRef.current?.();
      }
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
  }, [autoCloseMs, isPersistent]);

  return (
    <Box
      role="status"
      sx={{
        width: 'min(420px, calc(100vw - 32px))',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${styles.borderColor}`,
        borderRadius: '12px',
        background: styles.background,
        boxShadow: styles.shadow,
        overflow: 'hidden'
      }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1
          }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: styles.labelColor,
              pr: 1
            }}>
            {styles.label}
          </Typography>

          <IconButton
            aria-label="Close feedback"
            onClick={onClose}
            size="small"
            sx={{
              p: 0.35,
              mt: -0.25,
              mr: -0.35,
              color: styles.closeColor
            }}>
            {!isPersistent && <CloseIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>

        <Typography
          sx={{
            mt: 0.25,
            fontSize: '0.88rem',
            lineHeight: 1.35,
            color: styles.messageColor,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
          {detailText}
        </Typography>
      </Box>

      {!isPersistent && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            backgroundColor: styles.progressTrack,
            '& .MuiLinearProgress-bar': {
              backgroundColor: styles.progressBar
            }
          }}
        />
      )}
    </Box>
  );
}
